// src/services/followService.js - VERSIÓN CORREGIDA
import supabase from '../supabase';

class FollowService {
  // Verificar si un usuario sigue a otro
  async isFollowing(followingId) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('Usuario no autenticado para verificar seguimiento');
        return false;
      }

      console.log('🔍 Verificando si', user.id, 'sigue a', followingId);

      const { data, error } = await supabase
        .from('user_follows')
        .select('id')
        .eq('follower_id', user.id)
        .eq('following_id', followingId)
        .maybeSingle(); // ✅ Usar maybeSingle() en lugar de single()

      if (error) {
        console.error('Error verificando seguimiento:', error);
        return false;
      }

      const isFollowing = !!data;
      console.log('✅ Resultado seguimiento:', isFollowing);
      return isFollowing;

    } catch (error) {
      console.error('Error en isFollowing:', error);
      return false;
    }
  }

  // Seguir a un usuario
  async followProfile(followingId, followingType = 'technician') {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuario no autenticado');

      if (user.id === followingId) {
        throw new Error('No puedes seguirte a ti mismo');
      }

      console.log('👥 Siguiendo a:', followingId, 'tipo:', followingType);

      // Verificar si ya lo sigue
      const alreadyFollowing = await this.isFollowing(followingId);
      if (alreadyFollowing) {
        console.log('Ya sigues a este usuario');
        return true;
      }

      const { data, error } = await supabase
        .from('user_follows')
        .insert({
          follower_id: user.id,
          following_id: followingId,
          following_type: followingType
        })
        .select()
        .single();

      if (error) {
        console.error('Error al seguir usuario:', error);
        throw error;
      }

      console.log('✅ Usuario seguido exitosamente:', data);

      // Crear notificación para el usuario seguido (sin esperar)
      this.createFollowNotification(followingId, user.id).catch(err => 
        console.warn('Error creando notificación:', err)
      );

      return true;
    } catch (error) {
      console.error('Error en followProfile:', error);
      throw error;
    }
  }

  // Dejar de seguir a un usuario
  async unfollowProfile(followingId) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuario no autenticado');

      console.log('👋 Dejando de seguir a:', followingId);

      const { data, error } = await supabase
        .from('user_follows')
        .delete()
        .eq('follower_id', user.id)
        .eq('following_id', followingId)
        .select();

      if (error) {
        console.error('Error al dejar de seguir:', error);
        throw error;
      }

      console.log('✅ Dejado de seguir exitosamente:', data);
      return true;
    } catch (error) {
      console.error('Error en unfollowProfile:', error);
      throw error;
    }
  }

  // Obtener contador de seguidores
  async getFollowersCount(userId) {
    try {
      const { count, error } = await supabase
        .from('user_follows')
        .select('*', { count: 'exact', head: true })
        .eq('following_id', userId);

      if (error) {
        console.error('Error obteniendo contador de seguidores:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Error en getFollowersCount:', error);
      return 0;
    }
  }

  // Obtener contador de seguidos
  async getFollowingCount(userId) {
    try {
      const { count, error } = await supabase
        .from('user_follows')
        .select('*', { count: 'exact', head: true })
        .eq('follower_id', userId);

      if (error) {
        console.error('Error obteniendo contador de seguidos:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Error en getFollowingCount:', error);
      return 0;
    }
  }

  // Obtener lista de seguidores (puede incluir usuarios y empresas)
  async getFollowers(userId, limit = 50, offset = 0) {
    try {
      const { data, error } = await supabase
        .from('user_follows')
        .select(`
          follower_id,
          created_at,
          follower_user:user_profiles!follower_id(
            id,
            first_name,
            last_name,
            profile_image,
            specialization,
            city,
            country
          ),
          follower_company:company_profiles!follower_id(
            id,
            company_name,
            logo,
            main_category,
            city,
            country
          )
        `)
        .eq('following_id', userId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        console.error('Error obteniendo seguidores:', error);
        return [];
      }

      // ✅ PROCESAR DATOS COMBINANDO USER Y COMPANY PROFILES
      const processedData = data?.map(follow => {
        const isUser = !!follow.follower_user;
        const isCompany = !!follow.follower_company;
        
        return {
          ...follow,
          follower: isUser ? {
            ...follow.follower_user,
            type: 'user',
            display_name: `${follow.follower_user.first_name} ${follow.follower_user.last_name}`.trim(),
            image: follow.follower_user.profile_image
          } : isCompany ? {
            ...follow.follower_company,
            type: 'company',
            display_name: follow.follower_company.company_name,
            image: follow.follower_company.logo
          } : null
        };
      }).filter(follow => follow.follower !== null) || [];

      return processedData;
    } catch (error) {
      console.error('Error en getFollowers:', error);
      return [];
    }
  }

  // Obtener lista de seguidos (puede incluir usuarios y empresas)
  async getFollowing(userId, limit = 50, offset = 0) {
    try {
      const { data, error } = await supabase
        .from('user_follows')
        .select(`
          following_id,
          following_type,
          created_at,
          following_user:user_profiles!following_id(
            id,
            first_name,
            last_name,
            profile_image,
            specialization,
            city,
            country
          ),
          following_company:company_profiles!following_id(
            id,
            company_name,
            logo,
            main_category,
            city,
            country
          )
        `)
        .eq('follower_id', userId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        console.error('Error obteniendo seguidos:', error);
        return [];
      }

      // ✅ PROCESAR DATOS COMBINANDO USER Y COMPANY PROFILES
      const processedData = data?.map(follow => {
        const isUser = follow.following_type === 'technician' && !!follow.following_user;
        const isCompany = follow.following_type === 'company' && !!follow.following_company;
        
        return {
          ...follow,
          following: isUser ? {
            ...follow.following_user,
            type: 'user',
            display_name: `${follow.following_user.first_name} ${follow.following_user.last_name}`.trim(),
            image: follow.following_user.profile_image
          } : isCompany ? {
            ...follow.following_company,
            type: 'company',
            display_name: follow.following_company.company_name,
            image: follow.following_company.logo
          } : null
        };
      }).filter(follow => follow.following !== null) || [];

      return processedData;
    } catch (error) {
      console.error('Error en getFollowing:', error);
      return [];
    }
  }

  // Verificar múltiples usuarios de una vez (optimización)
  async checkMultipleFollowStatus(userIds) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return {};

      const { data, error } = await supabase
        .from('user_follows')
        .select('following_id')
        .eq('follower_id', user.id)
        .in('following_id', userIds);

      if (error) {
        console.error('Error verificando múltiples seguimientos:', error);
        return {};
      }

      // Convertir a objeto para acceso rápido
      const followStatus = {};
      userIds.forEach(id => {
        followStatus[id] = data?.some(follow => follow.following_id === id) || false;
      });

      return followStatus;
    } catch (error) {
      console.error('Error en checkMultipleFollowStatus:', error);
      return {};
    }
  }

  // Crear notificación de nuevo seguidor
  async createFollowNotification(userId, followerId) {
    try {
      console.log('📧 Creando notificación para:', userId, 'de:', followerId);
      
      let followerName = 'Un usuario';
      
      // ✅ PRIMERO INTENTAR BUSCAR EN user_profiles
      const { data: userProfile, error: userError } = await supabase
        .from('user_profiles')
        .select('first_name, last_name')
        .eq('id', followerId)
        .maybeSingle();

      if (!userError && userProfile) {
        followerName = `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() || 'Un usuario';
      } else {
        // ✅ SI NO SE ENCUENTRA, BUSCAR EN company_profiles
        const { data: companyProfile, error: companyError } = await supabase
          .from('company_profiles')
          .select('company_name')
          .eq('id', followerId)
          .maybeSingle();

        if (!companyError && companyProfile) {
          followerName = companyProfile.company_name || 'Una empresa';
        }
      }

      // ✅ VERIFICAR SI LA TABLA DE NOTIFICACIONES EXISTE ANTES DE INSERTAR
      const { error: notificationError } = await supabase
        .from('notifications')
        .insert({
          user_id: userId,
          title: 'Nuevo seguidor',
          message: `${followerName} ha comenzado a seguirte`,
          notification_type: 'follow',
          related_entity_type: 'user',
          related_entity_id: followerId
        });

      if (notificationError) {
        console.warn('Error insertando notificación:', notificationError);
        return;
      }

      console.log('✅ Notificación de seguimiento creada exitosamente');
    } catch (error) {
      console.warn('Error creando notificación de seguimiento:', error);
      // No lanzamos el error para no afectar el flujo principal
    }
  }

  // Buscar usuarios por nombre o especialización
  async searchUsers(query, limit = 20) {
    try {
      if (!query || query.trim().length < 2) return [];

      const { data, error } = await supabase
        .from('user_profiles')
        .select(`
          id,
          first_name,
          last_name,
          profile_image,
          specialization,
          city,
          country,
          bio
        `)
        .or(`first_name.ilike.%${query}%,last_name.ilike.%${query}%,specialization.ilike.%${query}%`)
        .not('first_name', 'is', null)
        .limit(limit);

      if (error) {
        console.error('Error buscando usuarios:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error en searchUsers:', error);
      return [];
    }
  }

  // Obtener usuarios sugeridos para seguir
  async getSuggestedUsers(limit = 10) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      // Obtener usuarios que no sigue y que tienen perfiles completos
      const { data: allUsers, error: usersError } = await supabase
        .from('user_profiles')
        .select(`
          id,
          first_name,
          last_name,
          profile_image,
          specialization,
          city,
          country,
          bio
        `)
        .neq('id', user.id)
        .not('first_name', 'is', null)
        .not('last_name', 'is', null)
        .limit(limit * 3); // Obtenemos más para filtrar

      if (usersError) {
        console.error('Error obteniendo usuarios:', usersError);
        return [];
      }

      // Obtener usuarios que ya sigue
      const { data: following, error: followingError } = await supabase
        .from('user_follows')
        .select('following_id')
        .eq('follower_id', user.id);

      if (followingError) {
        console.error('Error obteniendo seguidos:', followingError);
        // Continuar sin filtrar
        return allUsers?.slice(0, limit) || [];
      }

      const followingIds = following?.map(f => f.following_id) || [];
      const suggested = allUsers?.filter(profile => !followingIds.includes(profile.id)) || [];

      return suggested.slice(0, limit);
    } catch (error) {
      console.error('Error en getSuggestedUsers:', error);
      return [];
    }
  }
}

export const followService = new FollowService();